import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Download, Trash2, AlertTriangle } from "lucide-react";
import { ReleaseClients } from "@/components/security/ReleaseClients";

export function DataExportCard() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `root-and-rose-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="warm-card p-6 flex items-start justify-between gap-4 flex-wrap" data-testid="export-card">
      <div className="flex items-start gap-3">
        <Download className="w-6 h-6 text-amber-700 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-bold text-stone-900">Download my data</h3>
          <p className="text-sm text-stone-600 mt-0.5">A complete copy of your habits, check-ins, wellbeing logs, reflections, rewards, notes received, and your account activity log — as one JSON file.</p>
        </div>
      </div>
      <button data-testid="export-download" onClick={download} disabled={busy} className="amber-btn text-sm disabled:opacity-50">{busy ? "Preparing…" : "Download JSON"}</button>
    </div>
  );
}

export function DeleteAccountCard() {
  const { setUser, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const destroy = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete("/me", { data: { password, confirm } });
      setUser(false);
      toast.success("Your account and all of your data have been deleted.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="warm-card p-6 border-red-200 space-y-4" data-testid="delete-card">
      <div className="flex items-start gap-3">
        <Trash2 className="w-6 h-6 text-red-700 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-bold text-stone-900">Delete my account</h3>
          <p className="text-sm text-stone-600 mt-0.5">
            Permanently removes your account, habits, check-ins, wellbeing logs, reflections, rewards, reminders, community notes and buddy pairings. This cannot be undone.
            {user?.role === "clinician" && " Linked people you support must be released first — they keep everything and continue as solo users."}
          </p>
        </div>
      </div>
      {user?.role === "clinician" && <ReleaseClients />}
      {!open ? (
        <button data-testid="delete-open" onClick={() => setOpen(true)} className="px-4 py-2 rounded-full text-sm font-semibold bg-red-50 text-red-800 hover:bg-red-100">Delete my account…</button>
      ) : (
        <form onSubmit={destroy} className="rounded-2xl bg-red-50 border border-red-200 p-4 space-y-3" data-testid="delete-form">
          <div className="flex items-center gap-2 text-red-800 text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> This is permanent.</div>
          <input data-testid="delete-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password"
            className="w-full px-3 py-2 rounded-xl border border-red-200 text-sm focus:border-red-500 outline-none" />
          <input data-testid="delete-confirm" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Type DELETE to confirm'
            className="w-full px-3 py-2 rounded-xl border border-red-200 text-sm font-mono focus:border-red-500 outline-none" />
          <div className="flex gap-2">
            <button data-testid="delete-submit" type="submit" disabled={busy || confirm !== "DELETE" || !password} className="px-4 py-2 rounded-full text-sm font-semibold bg-red-700 text-white hover:bg-red-800 disabled:opacity-50">Delete everything</button>
            <button type="button" data-testid="delete-cancel" onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-semibold bg-white text-stone-700">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
